import React, { useState } from 'react'
import styled from 'styled-components'
import Colors from '../../../assets/styles/Colors'
import Responsive from '../../../assets/config/Responsive'
import Loader from '../../../components/Loader'
import ScreenHeader from '../../../components/v2/ScreenHeader'
import StartOver from '../../../components/v2/buttons/StartOver'
import MovingPrice from '../../../components/v2/MovingPrice'
import { useAppSelector } from '../../../store'
import { HealthState, clearHealthStore } from '../../../store/healthSlice'
import Title from '../../../components/v2/Title'
import { useTranslation } from 'react-i18next'
import MasterCardIcon from '../../../assets/icons/JSX/MasterCardIcon'
import OtherCard from '../../../assets/icons/JSX/OtherCard'
import NavigationButton from '../../../components/v2/buttons/NavigationButton'
import { useNavigate } from 'react-router-dom'
import BaseModal from '../../../components/v2/modals/BaseModal'
import Button from '../../../components/v2/buttons/Button'
import { useDispatch } from 'react-redux'
import BackgroundIcon from '../../../components/v2/BackgroundIcon'

const HealthPayment = () => {
  //
  const { t } = useTranslation()
  //
  const navigate = useNavigate()
  //
  const dispatch = useDispatch()
  //
  // selected payment method
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | undefined>(undefined)
  //
  const [startOverModal, setStartOverModal] = useState<boolean>(false)

  const { filter } = useAppSelector((data: { health: HealthState }) => data.health)

  const MainLoading = false
  return MainLoading ? (
    <Loader />
  ) : (
    <Wrapper>
      <BaseModal isOpen={startOverModal} onClose={() => setStartOverModal(!startOverModal)}>
        <StartOverDiv>
          <h5>{t('areyouSureTostartOver')}</h5>
          <div>
            <Button
              fullWidth={true}
              text={t('yes')}
              buttonType="primary"
              onClick={() => [dispatch(clearHealthStore()), navigate('/health-compare-companies')]}
            />
            <Button
              fullWidth={true}
              text={t('no')}
              buttonType="transparent"
              onClick={() => setStartOverModal(!startOverModal)}
            />
          </div>
        </StartOverDiv>
      </BaseModal>

      <ScreenHeader
        rightSideChildren={<StartOver text={t('startOver')} onClick={() => setStartOverModal(!startOverModal)} />}
      />

      <MovingPrice
        price={
          filter?.paymentSchedule?.type === 'MONTHLY'
            ? filter.offer?.calculatedPrices.monthly
            : filter.offer?.calculatedPrices?.annual
        }
      />

      <Content>
        <Title text={t('chooseCard')} />

        <Cards>
          <PaymentMethodCard
            $checked={selectedPaymentMethod === 'master' ? true : false}
            onClick={() => setSelectedPaymentMethod('master')}
          >
            <PaymentMethodCardIcon>
              <MasterCardIcon />
            </PaymentMethodCardIcon>
            <PaymentMethodCardText>Master Card</PaymentMethodCardText>
            <PaymentMethodCardSale>-50%</PaymentMethodCardSale>
            <CheckedStyle $checked={selectedPaymentMethod === 'master' ? true : false} />
          </PaymentMethodCard>

          <PaymentMethodCard
            $checked={selectedPaymentMethod === 'other' ? true : false}
            onClick={() => setSelectedPaymentMethod('other')}
          >
            <PaymentMethodCardIcon>
              <OtherCard />
            </PaymentMethodCardIcon>
            <PaymentMethodCardText>{t('paymentOtherWay')}</PaymentMethodCardText>
            <CheckedStyle $checked={selectedPaymentMethod === 'other' ? true : false} />
          </PaymentMethodCard>
        </Cards>

        <Buttons>
          <NavigationButton
            text={`${t('back')}`}
            buttonType="prev"
            onClick={() => navigate('/health-policy-registration')}
          />
        </Buttons>
      </Content>

      <BackgroundIcon/>
    </Wrapper>
  )
}

export default HealthPayment

const Wrapper = styled.div`
  min-height: 100vh;
  background-color: ${Colors.backgroundGrey};
  padding-bottom: 20px;
`
const Content = styled.div`
  position: relative;
  margin-top: 58px;
  z-index: 2;
  padding-inline: 20px;
  width: 100%;

  ${Responsive.mobile} {
    margin-top: 26px;
  }
`
const Cards = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin-block: 32px;
`
const PaymentMethodCard = styled.div<{ $checked: boolean }>`
  height: 96px;
  width: 100%;
  max-width: 464px;
  background-color: ${Colors.white};
  border-radius: 8px;
  margin-bottom: 20px;
  box-shadow: 0 4px 50px 0 rgba(0, 0, 0, 0.04);
  display: flex;
  align-items: center;
  padding-inline: 48px;
  position: relative;
  outline: ${({ $checked }) => ($checked ? `1px solid ${Colors.primaryBlack}` : 'unset')};
  transition: all 200ms ease-out;

  &:hover {
    outline: 1px solid ${Colors.primaryBlack};
    cursor: pointer;
    transition: all 200ms ease-out;
  }

  ${Responsive.mobile} {
    padding-inline: 30px;
  }
`
const PaymentMethodCardIcon = styled.div`
  margin-right: 30px;
  svg {
    width: 56px;
    height: 36px;
  }

  ${Responsive.mobile} {
    margin-right: 14px;

    svg {
      height: 32px;
      width: 56px;
    }
  }
`
const PaymentMethodCardText = styled.div`
  margin-right: 22px;
  font-size: 18px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: ${Colors.primaryBlack};

  ${Responsive.mobile} {
    margin-right: 17px;
    font-size: 17px;
  }
`
const PaymentMethodCardSale = styled.div`
  background-color: ${Colors.lightRed};
  padding: 5px 9px;
  display: grid;
  place-items: center;
  font-size: 18px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: 1px;
  color: ${Colors.primaryRed};
  border-radius: 2px;

  ${Responsive.mobile} {
    font-size: 14px;
    padding: 5px 6px;
  }
`
const CheckedStyle = styled.div<{ $checked: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  position: absolute;
  top: 16px;
  right: 16px;
  border: ${({ $checked }) => ($checked ? 'unset' : `solid 1px ${Colors.nevadaGrey}`)};
  box-shadow: ${({ $checked }) => ($checked ? `inset 0px 0px 0px 11px ${Colors.primaryRed}` : 'unset')};

  ${Responsive.mobile} {
    width: 26px;
    height: 26px;
    box-shadow: ${({ $checked }) => ($checked ? `inset 0px 0px 0px 9px ${Colors.primaryRed}` : 'unset')};
  }
`
const StartOverDiv = styled.div`
  h5 {
    text-align: center;
    color: ${Colors.primaryBlack};
  }

  div {
    margin-top: 50px;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: 1fr;
    grid-column-gap: 20px;
    grid-row-gap: 0px;
  }

  ${Responsive.mobile}{
    div{
      grid-template-columns: repeat(2, 130px);
      justify-content: center;
    }
  }
`

const Buttons = styled.div`
  display: flex;
  gap: 20px;
  width: min-content;
  margin-top: 48px;
  margin-inline: auto;
  button {
    width: 175px;
  }

  ${Responsive.mobile} {
    margin-top: 40px;
    gap: 16px;
    button {
      width: 140px;
    }
  }
`
